import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { buildGrid } from '../../lib/bookingGrid.js';

import { resolveLook } from '../../lib/bookingSlots.js';

function useColumnsPerPage() {
  const pick = () => (typeof window !== 'undefined' && window.innerWidth < 640 ? 3 : 6);
  const [n, setN] = useState(pick);
  useEffect(() => {
    const on = () => setN(pick());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return n;
}

/**
 * Multi-day booking grid: one column per day, one button per time. Open times are
 * solid in the agent's slot colour; times they did not offer are faded. Colours
 * come from the agent's Booking Page appearance settings.
 * @param {{slots: string[], timeZone?: string, appearance?: object, stepMinutes?: number, daysOut?: number,
 *   selected?: string|null, onSelect?: (iso: string) => void, now?: Date,
 *   compact?: boolean}} props - compact: smaller buttons in a fixed-height scroll area (settings preview).
 *   `appearance` should already be resolved (see resolveLook).
 * @returns {JSX.Element}
 */
export default function BookingGrid({ slots, timeZone, appearance, stepMinutes = 30, daysOut = 7, selected = null, onSelect, now, compact = false }) {
  const look = resolveLook(appearance);
  const perPage = useColumnsPerPage();
  const [page, setPage] = useState(0);
  const { columns, rows, cells } = useMemo(() => buildGrid({ slots, timeZone, stepMinutes, daysOut, now }), [slots, timeZone, stepMinutes, daysOut, now]);

  // New availability (e.g. after a save or a time-zone switch) restarts at the first day.
  useEffect(() => { setPage(0); }, [slots, timeZone]);

  const pages = Math.max(1, Math.ceil(columns.length / perPage));
  const safePage = Math.min(page, pages - 1);
  const visible = columns.slice(safePage * perPage, safePage * perPage + perPage);
  const navStyle = { borderColor: look.fontColor, color: look.fontColor };
  const filled = look.slotAppearance === 'filled';

  if (!slots.length) {
    return <div className="py-8 text-center text-sm opacity-70" data-testid="no-slots">No times available right now. Please check back later.</div>;
  }

  return (
    <div data-testid="booking-grid" style={{ color: look.fontColor }}>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button type="button" aria-label="Previous days" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}
          className="py-1.5 rounded border flex items-center justify-center disabled:opacity-30 hover:bg-black/5 transition" style={navStyle}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" aria-label="Next days" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}
          className="py-1.5 rounded border flex items-center justify-center disabled:opacity-30 hover:bg-black/5 transition" style={navStyle}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid gap-3 text-center pb-2 mb-3 border-b" style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`, borderColor: look.fontColor }}>
        {visible.map((c) => (
          <div key={c.key} data-testid={`col-${c.weekday}`}>
            <div className="font-semibold">{c.weekday}</div>
            <div className="text-xs opacity-80">{c.md}</div>
          </div>
        ))}
      </div>

      <div className={compact ? 'space-y-1.5 max-h-[340px] overflow-y-auto pr-1' : 'space-y-2'} data-testid="grid-rows">
        {rows.map((r) => (
          <div key={r.minutes} className={compact ? 'grid gap-2' : 'grid gap-3'} style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}>
            {visible.map((c) => {
              if (!c.available) {
                // Day with no times at all: say so once, at the top of the column.
                return r.minutes === rows[0].minutes
                  ? <div key={c.key} className="text-xs text-center pt-2">Not available.</div>
                  : <div key={c.key} />;
              }
              const iso = cells.get(`${c.key}|${r.minutes}`);
              const on = iso && iso === selected;
              return (
                <button key={c.key} type="button" disabled={!iso} onClick={() => iso && onSelect?.(iso)}
                  aria-label={iso ? `${c.weekday} ${c.md} ${r.label}` : undefined}
                  className={`${compact ? 'py-1 text-[11px]' : 'py-2 text-xs'} px-1 rounded-md font-bold transition ${iso ? `hover:-translate-y-0.5 ${filled ? 'shadow-md hover:shadow-lg' : ''}` : 'cursor-default'}`}
                  style={{
                    backgroundColor: look.slotColor,
                    color: look.slotFontColor,
                    border: `1px solid ${look.slotBorderColor}`,
                    opacity: iso ? 1 : 0.35,
                    outline: on ? `3px solid ${look.fontColor}` : 'none',
                  }}>
                  {r.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
